<template>
    <div class="widget widget-situation" ref="rootEl">
        <div class="widget-situation__inner">
            <div class="widget-situation__content" v-bind:class="{ 'widget-situation__content--expanded': isLegendExpanded }">
                <h3 class="widget-situation__heading">
                    {{ $t('widgetSituation_Heading') }}
                </h3>
                <div class="widget-frequency__error" v-if="hasFetchError" role="alert">
                    {{ $t('widgetSituation_FetchError') }}
                </div>
                <p>{{ $t('widgetSituation_Body') }}</p>
                <div v-if="legend" >
                    <a href="#"
                        ref="expandLegendButtonEl"
                        class="widget-situation__expand-legend-button"
                        role="button"
                        :aria-expanded="isLegendExpanded.toString()"
                        :aria-controls="legendId"
                        v-on:click="clickExpandLegend"
                        v-on:keydown.space.prevent="clickExpandLegend"
                        v-if="!isLegendExpanded"
                    >
                        <span aria-hidden="true">+</span>
                        <span>{{ $t('widgetSituation_ReadMoreText') }}</span>
                    </a>
                    <div :id="legendId" class="widget-situation__legend" :aria-hidden="(!isLegendExpanded).toString()">
                        <h4 class="widget-situation__legend__heading">
                            {{ $t('widgetSituation_ReadMoreHeading') }}
                        </h4>
                        <div v-for="(row, index) in legend" :key="index" class="widget-situation__legend__row">
                            <h5 class="widget-situation__legend__row-heading">
                                <span class="widget-situation__legend__color" :style="`background-color: ${row.color};`" aria-hidden="true"></span>
                                <strong>{{ row.name }}</strong>
                            </h5>
                            <p v-if="row.description !== ''">{{ row.description }}</p>
                        </div>
                        <a href="#"
                            class="widget-situation__expand-legend-button"
                            role="button"
                            :aria-expanded="isLegendExpanded.toString()"
                            :aria-controls="legendId"
                            v-on:click="clickCollapseLegend"
                            v-on:keydown.space.prevent="clickCollapseLegend"
                            v-if="isLegendExpanded"
                        >
                            <span aria-hidden="true">–</span>
                            <span>{{ $t('widgetSituation_ReadLessText') }}</span>
                        </a>
                    </div>
                </div>
            </div>
            <div class="widget-situation__map">
                <Map :areas="coloredAreas" :legend="legend" :clickable="false" />
            </div>
            <div class="widget-situation__link">
                <a :href="widgetUrl">{{ $t('widgetSituation_Link') }}</a>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref } from 'vue';
    import { fetchSituations } from '../service.js';
    import Map from './Map.vue';

    const FETCH_INTERVAL = 1000 * 60; // Default fetch interval, 60 seconds
    const MIN_FETCH_INTERVAL = 1000 * 10; // 10 seconds

    const props = defineProps({
        areas: {
            type: Array,
            default: () => [],
        },
        widgetUrl: {
            type: String,
            default: '',
        },
        fetchInterval: {
            type: [Number, String],
            default: null,
        },
        powerSituationLegends: {
            type: Array,
            default: null,
        },
        powerSituationReadMores: {
            type: Array,
            default: () => [],
        },
        lang: {
            type: String,
            default: '',
        },
    });

    const instance = getCurrentInstance();
    const id = `WidgetSituation-${instance ? instance.uid : 'unknown'}`;
    const legendId = `${id}-legend`;
    const interval = ref(null);
    const situations = ref(null);
    const hasFetchError = ref(false);
    const isLegendExpanded = ref(false);
    const rootEl = ref(null);
    const expandLegendButtonEl = ref(null);

    /**
     * Colored areas
     */
    const coloredAreas = computed(() => {
        return (props.areas || []).map(area => {
            const nextArea = {
                ...area,
            };

            const situation = (situations.value || []).find(currentSituation => currentSituation.elspotId === nextArea.id);

            if(situation) {
                nextArea.color = getColor(situation.powerSituation);
                nextArea.tooltip = situation.description;
            }

            return nextArea;
        });
    });

    /**
     * Legend
     */
    const legend = computed(() => {
        if(!props.powerSituationLegends) {
            return null;
        }

        return props.powerSituationLegends.map(item => {
            const readMore = (props.powerSituationReadMores || []).find(currentReadMore => currentReadMore.id === item.id);

            return {
                name: item.name,
                color: getColor(item.id),
                description: readMore ? readMore.description : '',
            };
        });
    });

    /**
     * Fetch data
     */
    async function fetchData() {
        try {
            hasFetchError.value = false;

            situations.value = await fetchSituations(props.lang);
        } catch(error) {
            hasFetchError.value = true;
            situations.value = null;

            console.error(error);
        }
    }

    /**
     * Click expand legend
     */
    function clickExpandLegend(event) {
        event.preventDefault();

        isLegendExpanded.value = true;
    }

    /**
     * Click collapse legend
     */
    async function clickCollapseLegend(event) {
        event.preventDefault();

        if(rootEl.value) {
            rootEl.value.scrollIntoView();
        }

        isLegendExpanded.value = false;

        await nextTick();

        expandLegendButtonEl.value && expandLegendButtonEl.value.focus();
    }

    onMounted(() => {
        fetchData();

        const nextFetchInterval = props.fetchInterval || FETCH_INTERVAL;

        if(nextFetchInterval < MIN_FETCH_INTERVAL) {
            console.error('Fetch interval is too low:', nextFetchInterval);

            fetchData();
            return;
        }

        interval.value = setInterval(fetchData, nextFetchInterval);
    });

    onUnmounted(() => {
        clearInterval(interval.value);
    });

    const NORMAL = 'Normal';
    const PRESSED = 'Pressed';
    const STRAINED = 'Strained';
    const SEVERE = 'Severe';
    const RATIONED = 'Rationed';

    const GREEN = '#23aa5f';
    const YELLOW = '#FBE734';
    const BROWN = '#FBB034';
    const RED = '#DF1920';
    const BLACK = '#000000';

    function getColor(powerSituation) {
        switch(powerSituation) {
            case NORMAL:
                return GREEN;
            case PRESSED:
                return YELLOW;
            case STRAINED:
                return BROWN;
            case SEVERE:
                return RED;
            case RATIONED:
                return BLACK;
        }
    }
</script>
